import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { ErrorType } from 'src/app/rek-client-app/shared/models/error-type.model';
import { TranslationService } from 'src/metronic/core/_base/layout';
import { SweetAlertOptions } from 'sweetalert2';
import { LoggingService } from './logging.service';

@Injectable({
  providedIn: 'root'
})
export class ErrorService {

  constructor(
    private logger: LoggingService,
    private translationService: TranslationService
  ) { }

  getClientMessage(error: Error): string {
    if (!navigator.onLine) {
      return 'No Internet Connection';
    }
    return error.message ? error.message : error.toString();
  }

  getClientStack(error: Error): string {
    return error.stack;
  }

  getServerMessage(error: HttpErrorResponse): string {
    return error.message;
  }

  getServerStack(error: HttpErrorResponse): string {
    // handle stack trace
    return error?.error?.toString() ?? 'No stack trace available';
  }

  handleError(httpErrorResponse: HttpErrorResponse): Observable<never> {
    const error = httpErrorResponse.error;
    let errorType: ErrorType;

    if (error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.message);
      errorType = ErrorType.CLIENT;
    }
    else {
      // The backend returned an unsuccessful response code.
      errorType = ErrorType.SERVER;

      this.logger.logError({
        message: this.getServerMessage(httpErrorResponse),
        stackTrace: this.getServerStack(httpErrorResponse)
      });
    }

    // return an observable with an error data
    return throwError(errorType);
  }

  getGenericErrorDataToDisplay(errorType: ErrorType): Observable<SweetAlertOptions> {
    const toTranslate: string[] = [
      'SHARED.ERRORS.GENERIC-SWAL-ERROR.CLIENT-ERROR-TITLE',
      'SHARED.ERRORS.GENERIC-SWAL-ERROR.CLIENT-ERROR-TEXT',
      'SHARED.ERRORS.GENERIC-SWAL-ERROR.SERVER-ERROR-TITLE',
      'SHARED.ERRORS.GENERIC-SWAL-ERROR.SERVER-ERROR-TEXT',
      'SHARED.ERRORS.GENERIC-SWAL-ERROR.CODE-ERROR-TITLE',
      'SHARED.ERRORS.GENERIC-SWAL-ERROR.CODE-ERROR-TEXT',
      'SHARED.ERRORS.GENERIC-SWAL-ERROR.RETRY',
      'SHARED.ERRORS.GENERIC-SWAL-ERROR.CANCEL'
    ];

    return this.translationService
      .getTranslationFor(toTranslate)
      .pipe(
        map((translated: { [key: string]: string; }) => {
          let swalTitle: string;
          let swalText: string;
          const retry = translated['SHARED.ERRORS.GENERIC-SWAL-ERROR.RETRY'];
          const cancel = translated['SHARED.ERRORS.GENERIC-SWAL-ERROR.CANCEL'];

          if (errorType === ErrorType.CLIENT) {
            swalTitle = translated['SHARED.ERRORS.GENERIC-SWAL-ERROR.CLIENT-ERROR-TITLE'];
            swalText = translated['SHARED.ERRORS.GENERIC-SWAL-ERROR.CLIENT-ERROR-TEXT'];
          }
          else if (errorType === ErrorType.SERVER) {
            swalTitle = translated['SHARED.ERRORS.GENERIC-SWAL-ERROR.SERVER-ERROR-TITLE'];
            swalText = translated['SHARED.ERRORS.GENERIC-SWAL-ERROR.SERVER-ERROR-TEXT'];
          }
          else if (errorType === ErrorType.CODE) {
            swalTitle = translated['SHARED.ERRORS.GENERIC-SWAL-ERROR.CODE-ERROR-TITLE'];
            swalText = translated['SHARED.ERRORS.GENERIC-SWAL-ERROR.CODE-ERROR-TEXT'];
          }
          else {
            throw new Error('Something\'s wrong with the translation configuration here..');
          }

          return {
            title: swalTitle,
            text: swalText,
            showCancelButton: true,
            showConfirmButton: true,
            confirmButtonText: retry,
            cancelButtonText: cancel
          };
        })
      );
  }
}
